import React, { useMemo } from "react";
import { UseFormReturn } from "react-hook-form";
import { ClientContactSection } from "./sections/ClientContactSection";
import { ClientAddressSection } from "./sections/ClientAddressSection";
import { ClientContractSection } from "./sections/ClientContractSection";
import { ClientNotesSection } from "./sections/ClientNotesSection";
import { ClientStatusSection } from "./sections/ClientStatusSection";
import type { EditClientForm } from "@/lib/core/crm/types/contacts";
import { FormSectionGroup } from "@/components/shared/forms/FormSectionGroup";
import { defineTaxonomySection } from "@/components/shared/forms/FormSectionGroup/section-taxonomy";

interface ClientEditFormSectionsProps {
  editForm: UseFormReturn<EditClientForm>;
  /** @deprecated retained for backward-compat; no longer used. FormSectionGroup owns open/close. */
  openSections?: { [key: string]: boolean };
  /** @deprecated retained for backward-compat; no longer used. */
  toggleSection?: (key: string) => void;
}

/**
 * Codified progressive-disclosure layout for the Edit Client modal.
 * Wraps each existing `Client*Section` in headerless mode and lets
 * `FormSectionGroup` drive the accordion behavior.
 * See plan `the-log-activity-modal-enumerated-whisper.md`.
 */
export const ClientEditFormSections: React.FC<ClientEditFormSectionsProps> = ({ editForm }) => {
  const sections = useMemo(
    () => [
      defineTaxonomySection<EditClientForm>("core", {
        title: "Contact information",
        summary: "Name, email, phone, website, industry",
        kind: "always",
        render: (f) => <ClientContactSection editForm={f} headerless />,
      }),
      defineTaxonomySection<EditClientForm>("location", {
        title: "Address",
        render: (f) => <ClientAddressSection editForm={f} headerless />,
        hasValue: (f) =>
          Boolean(
            f.watch("address") ||
              f.watch("city") ||
              f.watch("state") ||
              f.watch("zip_code") ||
              f.watch("country")
          ),
      }),
      defineTaxonomySection<EditClientForm>("custom", {
        title: "Contract & financial",
        summary: "Contract type, rates, payment terms, tax ID",
        render: (f) => <ClientContractSection editForm={f} headerless />,
        hasValue: (f) =>
          Boolean(
            f.watch("contract_type") ||
              f.watch("hourly_rate") ||
              f.watch("monthly_rate") ||
              f.watch("payment_terms") ||
              f.watch("tax_id")
          ),
      }),
      defineTaxonomySection<EditClientForm>("description", {
        title: "Notes",
        render: (f) => <ClientNotesSection editForm={f} headerless />,
        hasValue: (f) => Boolean(f.watch("notes")),
      }),
      defineTaxonomySection<EditClientForm>("classification", {
        title: "Status",
        summary: "Active / inactive",
        render: (f) => <ClientStatusSection editForm={f} headerless />,
        // Always interesting; show closed by default but auto-expand if inactive
        hasValue: (f) => f.watch("is_active") === false,
      }),
    ],
    []
  );

  return <FormSectionGroup form={editForm} sections={sections} />;
};
